import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, Loader2, Database, Eye, EyeOff, Sun, Moon } from 'lucide-react';
import useStore from '../store/useStore';
import enactLogo from '../assets/LOGOTIPO-1@2x.png';

export default function Register() {
  const [form, setForm] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });
  const [roles, setRoles] = useState({ 'data-provider': false, 'data-consumer': false });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { theme, toggleTheme } = useStore();

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleRoleChange = (role) => {
    setRoles(prev => ({ ...prev, [role]: !prev[role] }));
  };

  const roleLabels = {
    'data-provider': { label: 'Provider', desc: 'Publish & share data assets' },
    'data-consumer': { label: 'Consumer', desc: 'Browse & transfer data offers' },
  };

  const validate = () => {
    if (!form.firstName.trim()) return 'First name is required.';
    if (!form.lastName.trim()) return 'Last name is required.';
    if (!form.username.trim()) return 'Username is required.';
    if (!form.email.trim()) return 'Email is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Invalid email address.';
    if (form.password.length < 8) return 'Password must be at least 8 characters.';
    if (form.password !== form.confirmPassword) return 'Passwords do not match.';
    if (!roles['data-provider'] && !roles['data-consumer']) return 'Please select at least one role.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    const selectedRoles = Object.entries(roles)
      .filter(([, checked]) => checked)
      .map(([role]) => role);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username.trim(),
          email: form.email.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          password: form.password,
          roles: selectedRoles,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'Registration failed. Please try again.');
      }

      setSuccess('Account created successfully! Redirecting to login…');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 py-10 px-4 relative">
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-800 transition-colors login-toggle-btn"
      >
        {theme === 'dark'
          ? <Sun className="w-5 h-5 text-gray-400" />
          : <Moon className="w-5 h-5 text-gray-400" />
        }
      </button>

      <div className="card max-w-lg w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          <img src={enactLogo} alt="ENACT Logo" className="h-14 w-auto object-contain mx-auto mb-4"
            style={theme === 'light' ? { filter: 'invert(1) hue-rotate(180deg)' } : {}} />
          <h1 className="text-3xl font-bold text-white">Create Account</h1>
          <p className="text-gray-400 mt-2">Join the ENACT Dataspace platform</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">First Name <span className="text-red-400">*</span></label>
              <input
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                className="input"
                
              />
            </div>
            <div>
              <label className="label">Last Name <span className="text-red-400">*</span></label>
              <input
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                className="input"
                
              />
            </div>
          </div>

          {/* Username */}
          <div>
            <label className="label">Username <span className="text-red-400">*</span></label>
            <input
              type="text"
              name="username"
              value={form.username}
              onChange={handleChange}
              className="input"
              
              required
              autoFocus
            />
          </div>

          {/* Email */}
          <div>
            <label className="label">Email <span className="text-red-400">*</span></label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="input"
              
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="label">Password <span className="text-red-400">*</span></label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={form.password}
                onChange={handleChange}
                className="input pr-10"
                
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(p => !p)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-200"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="label">Confirm Password <span className="text-red-400">*</span></label>
            <input
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              className="input"
              
              required
            />
          </div>

          {/* Role selection */}
          <div>
            <label className="label">Role(s) <span className="text-red-400">*</span></label>
            <p className="text-xs text-gray-500 mb-3">Select one or both roles. You can have both provider and consumer access.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(roleLabels).map(([roleKey, { label, desc }]) => (
                <button
                  key={roleKey}
                  type="button"
                  onClick={() => handleRoleChange(roleKey)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all cursor-pointer
                    ${roles[roleKey]
                      ? 'border-blue-500 bg-blue-600/20 text-white'
                      : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                    }`}
                >
                  {roleKey === 'data-provider' ? <Database className="w-6 h-6" /> : <UserPlus className="w-6 h-6" />}
                  <span className="font-medium text-sm">{label}</span>
                  <span className="text-xs text-center opacity-70">{desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Error / Success */}
          {error && (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          {success && (
            <div className="bg-green-900/20 border border-green-800 rounded-lg p-3">
              <p className="text-green-400 text-sm">{success}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Creating account…
              </>
            ) : (
              <>
                <UserPlus size={20} />
                Create Account
              </>
            )}
          </button>
        </form>

        <p className="text-center text-sm text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
